import {Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography} from "@mui/material";
import React, {useRef, useState} from "react";
import css_buttons from "./css/CCButton.module.scss";
import css_fileUpload from "./css/FileUpload.module.scss";
import {Backup, Close, ErrorOutline, FileUpload, FolderOpen, TextSnippet} from "@mui/icons-material";
import EventBus from "../../util/EventBus";
import { DragEvent, MouseEvent } from "./types";
import {Blue5, Grey6} from "../../theme/styles";
import {Condition} from "../../util/Condition";
import EnlilAxiosRestAPI from "../../util/EnlilAxiosRestAPI";
import MemoryContext from "../../util/MemoryContext";
import {SessionContext} from "../../util/SessionContext";
import LinearProgressWithLabel from "../LinearProgressWithLabel";
import { Buffer } from 'buffer';
import {v4 as uuidv4} from "uuid";
import {Crc32} from "@aws-crypto/crc32";
import {
    CreateMultipartUploadCommand,
    UploadPartCommand,
    CompleteMultipartUploadCommand,
    AbortMultipartUploadCommand, S3Client,
} from "@aws-sdk/client-s3";
import moment from "moment-timezone";


export default function AttachmentUpload(props: any) {

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fileMetadata, setFileMetadata] = useState<any[]>([]);
    const [isDragOver, setIsDragOver] = React.useState<boolean>(false);
    const [isMouseOver, setIsMouseOver] = React.useState<boolean>(false);
    const [uploadStatus, setUploadStatus] = useState('idle'); // 'idle', 'uploading', 'success', 'error'
    const [validationErrors, setValidationErrors] = useState([]);
    const fileInputRef = useRef(null);

    const maxFiles = 5;
    const maxFileSize = 4 * 1024 * 1024 * 1024; // 4GB

    function cancelHandler() {
        EventBus.components.emit("project-plan-phase-attachment-applet-cancel");
    }

    function handleDragEnter(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (uploadStatus === 'idle') {
            setIsDragOver(true);
        } else {
            setIsDragOver(false);
        }
    }

    function handleDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        // @ts-ignore
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsDragOver(false);
        }
    }

    function handleMouseEnter(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();

        if (uploadStatus === 'idle') {
            setIsMouseOver(true);
        } else {
            setIsMouseOver(false);
        }
    }

    function handleMouseOver(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    function handleMouseLeave(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        setIsMouseOver(false);
    }

    function handleClick(event: MouseEvent) {
        if (fileInputRef.current) {
            // @ts-ignore
            fileInputRef.current.click();
        }
    }

    function handleDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();

        setIsDragOver(false);

        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) {
            handleFileSelect(files);
        }
    }

    function handleFileInputChange(event: any) {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            handleFileSelect(files);
        }
        event.target.value = '';
    }

    function handleFileSelect(files: any) {
        const newFiles = files.slice(0, maxFiles);
        const errors = validateFiles(newFiles);

        setValidationErrors(errors);
        if (errors.length === 0) {
            let newList: any[] = selectedFiles.concat(newFiles)
            let metadata = []
            for (let i = 0; i < newList.length; i++) {
                metadata.push({name: newList[i].name, status: "Ready for upload", progress: 0, total: newList[i].size});
            }
            setSelectedFiles(selectedFiles.concat(newFiles));
            setFileMetadata(metadata);
        }
        setUploadStatus('idle');
    }

    function validateFiles(files: any) {
        const errors: any = [];

        files.forEach((file: any) => {
            if (file.size > maxFileSize) {
                errors.push("" + file.name + ": File size exceeds 4GB limit");
            }

            selectedFiles.forEach((f: any) => {
                if (file.name === f.name) {
                    errors.push("" + file.name + ": File already selected for upload.");
                }
            })
            /*
            const extension = '.' + file.name.split('.').pop().toLowerCase();
            if (!acceptedTypes.includes(extension)) {
                errors[file.name] = 'File type not supported';
            }
            */
        });

        if (files.length > maxFiles) {
            files.slice(maxFiles).forEach((file: any) => {
                errors.push("" + file.name + ": Maximum " + maxFiles + " files allowed");
            });
        }

        return errors;
    }

    function formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    function handleDone() {
        EventBus.components.emit("project-plan-phase-attachment-applet-done");
    }

    let sessionContext = new SessionContext()

    const PART_SIZE = 5 * 1024 * 1024; // 5 MB minimum for multipart upload

   // @ts-ignore
    const s3Client = new S3Client({
        region: "us-west-2",
        credentials: {
            accessKeyId: sessionContext.getS3AccessKeyId(),
            secretAccessKey: sessionContext.getS3SecretAccessKey(),
            sessionToken: sessionContext.getS3SessionToken()
        },
    });

    async function computeCRC32(buffer: Uint8Array): Promise<string> {
        const crc32 = new Crc32();
        crc32.update(buffer);
        const crcNumber = crc32.digest(); // returns a number

        // Convert the 32-bit number into a 4-byte big-endian Uint8Array
        const bytes = new Uint8Array(4);
        bytes[0] = (crcNumber >>> 24) & 0xff;
        bytes[1] = (crcNumber >>> 16) & 0xff;
        bytes[2] = (crcNumber >>> 8) & 0xff;
        bytes[3] = crcNumber & 0xff;

        // Return base64-encoded CRC32 string
        return btoa(String.fromCharCode(...bytes));
    }

    async function uploadMultipartWithChecksum(file: File, bucket: string, key: string) {
        const totalParts = Math.ceil(file.size / PART_SIZE);
        const parts: { PartNumber: number; ETag: string }[] = [];

        // Step 1: Initiate the multipart upload with ChecksumAlgorithm
        const createResponse = await s3Client.send(
            new CreateMultipartUploadCommand({
                Bucket: bucket,
                Key: key,
                ChecksumAlgorithm: "CRC32",
            })
        );

        const uploadId = createResponse.UploadId;
        if (!uploadId) throw new Error("UploadId missing from create response");

        try {
            for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
                const start = (partNumber - 1) * PART_SIZE;
                const end = Math.min(file.size, start + PART_SIZE);
                const blob = file.slice(start, end);

                const arrayBuffer = await blob.arrayBuffer();
                const uint8 = new Uint8Array(arrayBuffer);

                const checksumCRC32 = await computeCRC32(uint8);

                console.log(`Uploading part ${partNumber}/${totalParts} with CRC32: ${checksumCRC32}`);

                let element = fileMetadata.find((element) => element.name === file.name);
                element.status = "Uploading...";
                element.progress = partNumber * PART_SIZE;
                setFileMetadata(structuredClone(fileMetadata))

                const uploadPartResponse = await s3Client.send(
                    new UploadPartCommand({
                        Bucket: bucket,
                        Key: key,
                        UploadId: uploadId,
                        PartNumber: partNumber,
                        Body: blob,
                        ChecksumCRC32: checksumCRC32,
                    })
                );

                if (!uploadPartResponse.ETag) throw new Error("ETag missing from part upload response");

                parts.push({
                    PartNumber: partNumber,
                    ETag: uploadPartResponse.ETag,
                });
            }

            // Step 3: Complete the upload
            const completeResponse = await s3Client.send(
                new CompleteMultipartUploadCommand({
                    Bucket: bucket,
                    Key: key,
                    UploadId: uploadId,
                    MultipartUpload: {
                        Parts: parts,
                    },
                })
            );

            let element = fileMetadata.find((element) => element.name === file.name);
            element.status = "Done";
            setFileMetadata(structuredClone(fileMetadata))

            console.log("Upload complete:", completeResponse.Location || completeResponse.Key);
            return completeResponse;
        } catch (err) {
            let element = fileMetadata.find((element) => element.name === file.name);
            element.status = "Error";
            element.error = err;
            setFileMetadata(structuredClone(fileMetadata))

            try {
                await s3Client.send(
                    new AbortMultipartUploadCommand({
                        Bucket: bucket,
                        Key: key,
                        UploadId: uploadId,
                    })
                );
            } catch (err2) {
                console.log("Failed to upload part upload", err2);
            }
        }
    }

    function doTheDamnUploadAlready() {
        const { Upload } = require("@aws-sdk/lib-storage");

        setUploadStatus('uploading')

        async function calculateCRC32Base64(file: any) {
            const crc32 = new Crc32();
            const chunkSize = 64 * 1024;
            let offset = 0;

            while (offset < file.size) {
                const chunk = file.slice(offset, offset + chunkSize);
                const buffer = await chunk.arrayBuffer();
                crc32.update(new Uint8Array(buffer));
                offset += chunkSize;
            }

            const digest = crc32.digest(); // number
            const digestBuffer = new Uint8Array(4);
            digestBuffer[0] = (digest >>> 24) & 0xff;
            digestBuffer[1] = (digest >>> 16) & 0xff;
            digestBuffer[2] = (digest >>> 8) & 0xff;
            digestBuffer[3] = digest & 0xff;

            return Buffer.from(digestBuffer).toString('base64');
        }

        Promise.allSettled(
            selectedFiles.map((file: any) => new Promise(async resolve => {

                const condition = Condition();

                let now = moment.utc().toISOString();
                let realm = sessionContext.getRealm();
                let key = realm + "/" + now + "/" + file.name;

                async function uploadFileToS3(file: any) {
                    if (file.size <= 1024 * 1024 * 5) {
                        const upload = new Upload({
                            client: s3Client,
                            params: {
                                Bucket: "enlil-" + process.env.REACT_APP_ENLIL_ENV + "-be",
                                Key: key,
                                Body: file,
                                ContentType: file.type
                            }
                        });

                        upload.on("httpUploadProgress", (progress: any) => {
                            let element = fileMetadata.find((element) => element.name === file.name);
                            element.status = "Uploading...";
                            element.progress = progress.loaded;
                            setFileMetadata(structuredClone(fileMetadata))
                        });

                        try {
                            const result = await upload.done();
                            let element = fileMetadata.find((element) => element.name === file.name);
                            element.status = "Done";
                            setFileMetadata(structuredClone(fileMetadata))
                        } catch (err) {
                            let element = fileMetadata.find((element) => element.name === file.name);
                            element.status = "Error";
                            element.error = err;
                            setFileMetadata(structuredClone(fileMetadata))
                        }
                    } else {
                        await uploadMultipartWithChecksum(file, "enlil-" + process.env.REACT_APP_ENLIL_ENV + "-be", key)
                    }
                }

                await uploadFileToS3(file);

                let element = fileMetadata.find((element) => element.name === file.name)
                if (element.status !== "Error") {
                    let payload: any = {};

                    let attachment: any = {};

                    let attachmentUSID = uuidv4();
                    attachment["Name"] = file.name.substring(0, file.name.lastIndexOf("."));
                    attachment["S3Link"] = key;
                    attachment["Type"] = file.name.substring(file.name.lastIndexOf(".") + 1, file.name.length);
                    attachment["FileSize"] = file.size;
                    attachment["OwnerUSID"] = sessionContext.getEmployeeUSID();
                    attachment["USID"] = attachmentUSID;

                    // @ts-ignore
                    let phaseUSID = MemoryContext.components["ProjectPlanDetail-EO-PhaseUSID"]
                    payload.ShifamedMessage = {};
                    payload.ShifamedMessage["-idoc"] = "v1.ProjectPlanPhase-002";
                    payload.ShifamedMessage.ProjectPlanPhase = {
                        "USID": phaseUSID,
                        "ProjectPlanPhaseAttachment": {
                            "ProjectPlanPhaseUSID": phaseUSID,
                            "AttachmentUSID": attachmentUSID,
                            "Attachment": attachment,
                        }
                    };

                    let options = {
                        method: "PUT",
                        url: "t3/v1/project_plan_phases",
                        json: true,
                        data: payload,
                        headers: {
                            "idoc-request": "v1.ProjectPlanPhase-002",
                            "idoc-response": "v1.ProjectPlanPhase-002",
                        },
                    };
                    EnlilAxiosRestAPI(options)
                        .then(response => {
                            condition.notify("Done");
                        })
                        .catch(error => {
                            setUploadStatus('error')
                        });
                }

                resolve(await condition.lock);
            })),
        ).then(responses => {
            setUploadStatus('success')
        });
    }

    let done = true;
    let intermediate = true;

    if (fileMetadata.length === 0 || fileMetadata.find((element) => element.status !== "Done")) done = false;
    if (fileMetadata.find((element) => element.status === "Uploading...")) intermediate = false;

    let buttonHandler = (
        <DialogActions style={{marginBottom: "10px", width: "96%"}}>
            <Button disabled={done} className={css_buttons.redStandardButton} onClick={cancelHandler}>Cancel</Button>
            {done && selectedFiles.length > 0 ? (
                <Button
                    className={css_buttons.blueStandardButton} style={{marginLeft: "5px"}}
                    onClick={() => {
                        handleDone();
                    }}>
                    <Typography style={{fontSize: 14, fontWeight: 500, color: "#FFFFFF"}}>Done</Typography>
                </Button>
            ): (
                <Button
                    disabled={!intermediate || selectedFiles.length === 0}
                    className={css_buttons.blueStandardButton} style={{marginLeft: "5px"}}
                    onClick={() => {
                        doTheDamnUploadAlready()
                    }}
                    startIcon={<FileUpload sx={{color: "#FFFFFF", width: "20px", height: "20px"}}/>}>
                    <Typography style={{fontSize: 14, fontWeight: 500, color: "#FFFFFF"}}>Upload Files</Typography>
                </Button>
            )}
        </DialogActions>
    );

    return (
        <>
            <Dialog open={true} scroll="body">
                <DialogTitle><Typography style={{fontSize: 18, fontWeight: 500, color: "#000000"}}>Upload Files</Typography></DialogTitle>
                <DialogContent>
                    <div style={{borderTop: "1px solid #EDEFF3", borderBottom: "1px solid #EDEFF3", height: "100%", marginLeft: "-16px", marginRight: "-16px", marginTop: "10px"}}>
                        <div style={{display: "flex", flexDirection: "column", justifyContent: "center"}}>
                            <div className={isDragOver || isMouseOver ? css_fileUpload.fileUploadOver : css_fileUpload.fileUploadStandard} onDrop={(event) => handleDrop(event)} onDragEnter={(event) => handleDragEnter(event)} onDragOver={(event) => handleDragEnter(event)} onDragLeave={(event) => handleDragLeave(event)} onMouseEnter={(event) => handleMouseEnter(event)} onMouseOver={(event) => handleMouseOver(event)} onMouseLeave={(event) => handleMouseLeave(event)} onClick={(event) => handleClick(event)}>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", marginTop: "20px"}}>
                                    <div style={{width: "100px", height: "100px", borderRadius: "100px", backgroundColor: "#EDEFF3"}}>
                                        {isDragOver ? (
                                            <Backup style={{ marginTop: "15px", marginLeft: "20px", color: Blue5, width: "60px", height: "60px"}}/>
                                        ) : (
                                            <Backup style={{ marginTop: "15px", marginLeft: "20px", color: Grey6, width: "60px", height: "60px"}}/>
                                        )}
                                    </div>
                                </div>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", marginTop: "20px"}}>
                                    {isDragOver ? (
                                        <Typography style={{fontSize: "18px", fontWeight: 500, color: "#262837"}}>Drop files here</Typography>
                                    ) : (
                                        <Typography style={{fontSize: "18px", fontWeight: 500, color: "#262837"}}>Drop files to upload</Typography>
                                    )}
                                </div>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", marginTop: "5px"}}>
                                    <Typography style={{fontSize: "14px", fontWeight: 500, color: "#7B8291"}}>or </Typography>
                                    <Typography style={{fontSize: "14px", fontWeight: 500, color: "#0575AD"}}>&nbsp;browse files</Typography>
                                </div>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "center", marginTop: "20px"}}>
                                    <Typography style={{fontSize: "12px", fontWeight: 400, color: "#7B8291"}}>Supported formats: All file formats are supported</Typography>
                                </div>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
                                    <Typography style={{fontSize: "12px", fontWeight: 400, color: "#7B8291"}}>Maximum file size: 4GB per file</Typography>
                                </div>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
                                    <Typography style={{fontSize: "12px", fontWeight: 400, color: "#7B8291"}}>You can upload multiple files at once </Typography>
                                </div>
                            </div>
                            <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
                                <Button className={css_buttons.selectFileButton} style={{marginLeft: "5px", marginTop: "-5px"}} disabled={uploadStatus !== 'idle'}
                                    onClick={() => {
                                        // @ts-ignore
                                        fileInputRef.current?.click();
                                    }}
                                    startIcon={<FolderOpen sx={{color: uploadStatus === 'idle' ? "#7B8291" : "#A8A9AF", width: "30px", height: "30px"}}/>}>
                                    <Typography style={{fontSize: 18, fontWeight: 500}}>Choose Files</Typography>
                                </Button>
                                <input ref={fileInputRef} type="file" multiple accept={"*"} onChange={handleFileInputChange} style={{display: "none"}}/>
                            </div>
                            <div style={{marginLeft: "30px"}}>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", marginTop: "30px"}}>
                                    <Typography style={{fontSize: "16px", fontWeight: 500, color: "#262837"}}>Upload Requirements</Typography>
                                </div>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                                    <ul style={{marginTop: "0px", marginLeft: "-20px"}}>
                                        <li>
                                            <Typography style={{fontSize: "12px", fontWeight: 400, color: "#7B8291"}}>Supported formats: All file formats are supported</Typography>
                                        </li>
                                        <li>
                                            <Typography style={{fontSize: "12px", fontWeight: 400, color: "#7B8291"}}>Maximum file size: 4GB per file</Typography>
                                        </li>
                                        <li>
                                            <Typography style={{fontSize: "12px", fontWeight: 400, color: "#7B8291"}}>Maximum 5 files at once</Typography>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            {validationErrors.length > 0 ? (
                                <div style={{marginLeft: "30px", marginBottom: "10px"}}>
                                    <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", marginTop: "10px"}}>
                                        <Typography style={{fontSize: "16px", fontWeight: 500, color: "#FF0000"}}>Errors ({validationErrors.length})</Typography>
                                    </div>
                                    {validationErrors.map((error: any, index) => (
                                        <div key={index} style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", border: "1px solid #EDEFF3", marginTop: "10px", borderRadius: "10px", marginRight: "20px", padding: "5px 0 5px 0"}}>
                                            <div style={{width: "33px", height: "33px", backgroundColor: "#EDEFF3", borderRadius: "5px", margin: "5px 5px 5px 10px"}}>
                                                <ErrorOutline sx={{color: "#FF0000", width: "18px", height: "18px", marginTop: "7px", marginLeft: "8px"}}/>
                                            </div>
                                            <div style={{display: "flex", flexDirection: "column"}}>
                                                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", marginTop: "10px"}}>
                                                    <Typography style={{fontSize: "14px", fontWeight: 500, color: "#262837"}}>{error}</Typography>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            <div style={{marginLeft: "30px", marginBottom: "10px"}}>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", marginTop: "10px"}}>
                                    <Typography style={{fontSize: "16px", fontWeight: 500, color: "#262837"}}>Selected Files ({selectedFiles.length})</Typography>
                                </div>
                                {selectedFiles.map((file: any, index) => (
                                    <div key={index} style={{display: "flex", flexDirection: "column", border: "1px solid #EDEFF3", marginTop: "10px", borderRadius: "10px", marginRight: "20px", padding: "5px 0 5px 0"}}>
                                        <div key={file.name + index} style={{display: "flex", flexDirection: "row", justifyContent: "space-between"}}>
                                            <div key={file.name + index} style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", width: "500px"}}>
                                                <div key={1} style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                                                    <div style={{width: "33px", height: "33px", backgroundColor: "#EDEFF3", borderRadius: "5px", margin: "5px 5px 5px 10px"}}>
                                                        <TextSnippet sx={{color: "#A8A9AF", width: "18px", height: "18px", marginTop: "7px", marginLeft: "8px"}}/>
                                                    </div>
                                                    <div style={{display: "flex", flexDirection: "column"}}>
                                                        <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", marginTop: "3px"}}>
                                                            <Typography style={{fontSize: "14px", fontWeight: 500, color: "#262837"}}>{file.name}</Typography>
                                                        </div>
                                                        <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", marginTop: "-3px"}}>
                                                            <Typography style={{fontFamily: "courier", fontSize: "13px", fontWeight: 400, color: "#262837"}}>{formatFileSize(file.size)}</Typography>
                                                            <Typography style={{fontFamily: "courier", fontSize: "13px", fontWeight: 400, color: "#262837"}}>&nbsp;&#183;&nbsp;</Typography>
                                                            <Typography style={{fontSize: "14px", fontWeight: 500, color: (fileMetadata.find((element: any) => element.name === file.name)).status === "Error" ? "#FF0000" : "#00A044"}}>{(fileMetadata.find((element: any) => element.name === file.name)).status}</Typography>
                                                        </div>
                                                    </div>
                                                </div>
                                                {fileMetadata.find((element: any) => element.name === file.name).status !== "Ready for upload" ? (
                                                    <div key={2} style={{marginLeft: "10px", width: "150px", marginTop: "10px"}}>
                                                        <LinearProgressWithLabel value={(fileMetadata.find((element: any) => element.name === file.name).progress/file.size)*100} />
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div key={2} style={{display: "flex", flexDirection: "row", justifyContent: "flex-end"}}>
                                                <div style={{width: "33px", height: "33px", backgroundColor: "#FFFFFF", borderRadius: "5px", margin: "3px 10px 0px 0px"}}>
                                                    {fileMetadata.find((element: any) => element.name === file.name).status === "Ready for upload" ? (
                                                        <IconButton onClick={() => {
                                                            let index = selectedFiles.findIndex((element: any) => element.name === file.name)
                                                            let n = structuredClone(selectedFiles)
                                                            n.splice(index, 1)
                                                            setSelectedFiles(n)
                                                        }}>
                                                            <Close sx={{color: "#A8A9AF", width: "18px", height: "18px", marginTop: "0px", marginLeft: "0px"}}/>
                                                        </IconButton>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                        {fileMetadata.find((element: any) => element.name === file.name).status === "Error" ? (
                                            <div key={2} style={{marginLeft: "10px", width: "100%", marginTop: "5px"}}>
                                                <Typography style={{fontSize: "12px", fontWeight: 400, color: "#FF0000"}}>{fileMetadata.find((element: any) => element.name === file.name).error.message}</Typography>
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </DialogContent>
                {buttonHandler}
            </Dialog>
        </>
    );
}
